<template>
    <div v-if="badgeItems && badgeItems.length" class="tag-cloud-wrapper">
        <!-- Chips row -->
        <div class="tag-cloud-chips-row">
            <div class="tag-cloud-meta d-flex flex-column d-lg-none flex-shrink-0">
                <span v-if="totalResultsText" class="filter-results-count">{{ totalResultsText }}</span>
                <button
                    @click.prevent="$emit('clear-filters')"
                    class="btn btn-link clear-btn"
                    :title="clearAllLabel"
                >
                    <span class="clear-btn__label">{{ clearAllLabel }}:</span>
                </button>
            </div>

            <button
                @click.prevent="$emit('clear-filters')"
                class="btn btn-link clear-btn d-none d-lg-block"
                :title="clearAllLabel"
            >
                <img
                    src="/images/lynkac/icons/ico-close.svg"
                    alt=""
                    class="icon icon--close"
                />
            </button>

            <button
                v-if="canScrollPrev"
                @click="scrollPrev"
                class="scroll-btn prev"
                aria-label="Scroll previous"
            >‹</button>

            <div ref="tagCloud" class="tag-cloud-inner">
                <span
                    v-for="badge in badgeItems"
                    :key="badge.filterName + badge.filterValue"
                    class="badge badge-filter d-inline-flex align-items-center"
                >
                    <span class="badge-filter__label">{{ getDisplayLabel(badge) }}</span>
                    <button
                        type="button"
                        class="badge-filter__remove-btn"
                        @click.prevent="$emit('remove-filter', badge.filterName, badge.filterValue, badge.optionId)"
                        :title="removeFilterLabel"
                    >
                        ×
                    </button>
                </span>
            </div>

            <button
                v-if="canScrollNext"
                @click="scrollNext"
                class="scroll-btn next"
                aria-label="Scroll next"
            >›</button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
    badgeItems: {
        type: Array,
        required: true
    },
    clearAllLabel: {
        type: String,
        required: true
    },
    removeFilterLabel: {
        type: String,
        required: true
    },
    totalResultsText: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['remove-filter', 'clear-filters']);

function getDisplayLabel(badge) {
    return badge.filterCategoryLabel
        ? `${badge.filterCategoryLabel}: ${badge.label}`
        : badge.label
}

const tagCloud = ref(null);
const canScrollPrev = ref(false);
const canScrollNext = ref(false);
const scrollAmount = 150;

function updateScrollButtons() {
    const el = tagCloud.value;
    if (!el) return;
    canScrollPrev.value = el.scrollLeft > 0;
    canScrollNext.value = el.scrollLeft + el.clientWidth < el.scrollWidth;
}

function scrollPrev() {
    tagCloud.value?.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
}

function scrollNext() {
    tagCloud.value?.scrollBy({ left: scrollAmount, behavior: 'smooth' });
}

onMounted(() => {
    nextTick(() => {
        const el = tagCloud.value;
        if (!el) return;
        el.addEventListener('scroll', updateScrollButtons);
        updateScrollButtons();
    });
});

onBeforeUnmount(() => {
    const el = tagCloud.value;
    if (el) {
        el.removeEventListener('scroll', updateScrollButtons);
    }
});
</script>

<style scoped>
.tag-cloud-wrapper {
    position: relative;
}

.tag-cloud-chips-row {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
}

.tag-cloud-meta {
    justify-content: flex-start;
}

.tag-cloud-inner {
    flex: 1;
    display: flex;
    gap: 0.5rem;
    overflow-x: auto;
    scroll-behavior: smooth;
}

.clear-btn {
    margin: 0;
    padding: 0 !important;
    font-size: inherit;
    line-height: 1;
    background: transparent;
    border: none;
    cursor: pointer;
    height: auto;
    min-height: 0;
}

.scroll-btn {
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.7);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    padding: 0.25rem 0.4rem;
    font-size: 1.25rem;
    line-height: 1;
    z-index: 1;
    transition: background 0.15s ease;
}

.scroll-btn:hover {
    background: rgba(255, 255, 255, 0.22);
    color: #ffffff;
}
.clear-btn__label {
    white-space: nowrap;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: -0.01em;
    color: #ffffff;
    opacity: 0.6;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.badge-filter__label {
    margin-right: 6px;
}
.badge-filter__remove-btn {
    background: transparent;
    border: none;
    color: currentColor;
    font-size: 1.5em;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    margin-bottom: 3px;
}
.badge-filter__remove-btn:hover {
    opacity: 0.7;
}
</style>
